import {
    EntityTypes,
    LookupDataEnums,
    ReportStatusTypesEnum,
    ReportUserTypesEnum,
} from '../../../../utilities/Constants';
import { IReportBaseModel, IReportUserModel } from '../../../../interfaces/Report/IReport';
import { useEffect, useState } from 'react';

import CheckmateSelect from '../../../../components/shared/CheckmateSelect';
import CheckmateSelectHelper from '../../../../utilities/CheckmateSelectHelper';
import { CollabActHistory } from '../../../../components/weekly-recap/CollabActHistory';
import { ICollabModalProps } from './CollabModalContext';
import { ICollabReportStatusModel } from '../../../../interfaces/ICollabReport';
import { IEntityLease } from '../../../../interfaces/IEntityLease';
import { IMultiSelectOption } from '../../../../interfaces/ILookup';
import { IValidation } from '../../../../interfaces/IError';
import { IconButton } from '../../../../shared/buttons/IconButton';
import { Modal } from 'react-bootstrap';
import { cloneDeep } from 'lodash';
import { showErrorToast } from '../../../../shared/ToastUtils';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useGetLookupData } from '../../../../shared/react-query-hooks/useGetLookupData';
import { useObtainEntityLeaseMutation } from '../../../../shared/react-query-hooks/mutations/useObtainEntityLeaseMutation';

enum FormFields {
    ReportName = 'REPORT_NAME',
    ReportStatus = 'REPORT_STATUS',
}

export interface ISaveReportModalData {
    alertGuid?: string;
    availableUsers: IMultiSelectOption[];
    closeButtonText: string;
    disableReaders?: boolean;
    executeSaveReport: (
        report: IReportBaseModel,
        reportUsers: IReportUserModel[],
        readers: IReportUserModel[],
        redirectOnSave?: boolean
    ) => void;
    leaseDuration: number;
    onClose: (modalHasOwnLease: boolean) => void;
    onCollabActClosed?: () => void;
    onLeaseObtained: (lease: IEntityLease) => void;
    onFailedToObtainLease: (currentLease: IEntityLease) => void;
    readonly: boolean;
    report: IReportBaseModel | undefined;
    reportCollaborators: IReportUserModel[];
    reportReaders: IReportUserModel[];
    showEditButton?: boolean;
    title?: string;
    userStatuses?: ICollabReportStatusModel[];
}

export const SaveReportModal: React.FC<ICollabModalProps<ISaveReportModalData, () => void>> = (
    props
) => {
    if (!props.open) return null;

    const auth = useAuthContext();
    const obtainEntityLeaseMutation = useObtainEntityLeaseMutation();
    const reportStatusTypes = useGetLookupData(LookupDataEnums.ReportStatusTypes);

    const [reportStatusOptions, setReportStatusOptions] = useState<IMultiSelectOption[]>([]);
    const [localReadonly, setLocalReadonly] = useState<boolean>(!!props.data?.readonly);
    const [collaborators, setCollaborators] = useState<IMultiSelectOption[]>(
        props.data?.availableUsers
            .filter((user) =>
                props.data?.reportCollaborators.map((user) => user.userGuid).includes(user.guid)
            )
            .map((user) => ({
                ...user,
                isFixed: true,
                readOnly: !props.data?.alertGuid ? user.guid === auth.user.guid : false,
            })) ?? []
    );
    const [readers, setReaders] = useState<IMultiSelectOption[]>(
        props.data?.availableUsers
            .filter((availUser) =>
                props.data?.reportReaders.map((user) => user.userGuid).includes(availUser.guid)
            )
            .map((user) => ({
                ...user,
                isFixed: true,
                readOnly: !props.data?.alertGuid ? user.guid === auth.user.guid : false,
            })) ?? []
    );
    const [originalReaders] = useState<IMultiSelectOption[]>(
        props.data?.availableUsers.filter((user) =>
            props.data?.reportReaders.map((user) => user.userGuid).includes(user.guid)
        ) ?? []
    );
    const [validation, setValidation] = useState<IValidation>({});
    const [reportName, setReportName] = useState<string>(props.data?.report?.name ?? '');
    const [reportStatusId, setReportStatusId] = useState<number | null>(
        props.data?.report?.status?.id ?? null
    );

    const validate = (): IValidation => {
        const errors: { [key: string]: string[] } = {};

        if (!reportName) {
            errors[FormFields.ReportName] = Array.isArray(errors[FormFields.ReportName])
                ? [...errors[FormFields.ReportName], 'Required']
                : ['Required'];
        }

        if (!reportStatusId) {
            errors[FormFields.ReportStatus] = Array.isArray(errors[FormFields.ReportStatus])
                ? [...errors[FormFields.ReportStatus], 'Required']
                : ['Required'];
        }

        return errors;
    };

    const handleSaveReport = async () => {
        const errors = validate();

        if (Object.keys(errors)?.length > 0) {
            setValidation(errors);
            return;
        }

        if (!reportStatusId || !reportName) return;

        const collabUsers = collaborators?.map((item: IMultiSelectOption) => ({
            userGuid: item.guid,
            reportUserTypeId: ReportUserTypesEnum.Collaborator,
        }));

        const reportReaders = readers?.map((item: IMultiSelectOption) => ({
            userGuid: item.guid,
            reportUserTypeId: ReportUserTypesEnum.Reader,
        }));

        if (props.data?.executeSaveReport) {
            props.data?.executeSaveReport(
                { ...props.data?.report, name: reportName, status: { id: reportStatusId } },
                collabUsers,
                reportReaders ?? [],
                !props.data?.report?.guid
            );
        }

        props.onClose();
    };

    const handleCloseClick = () => {
        // determines if the modal was opened with a lease or acquired its own
        //  if showEditButton is true, then the caller already had a lease - overloading showEditButton here.
        //  if localReadonly is false, then this modal's edit button was clicked and a lease was acquired
        const modalHasOwnLease = !!props.data?.showEditButton && !localReadonly;
        props.data?.onClose(modalHasOwnLease);
    };

    const handleCollaboratorsChange = (selectedOptions: IMultiSelectOption[]) => {
        const collaboratorsCopy = cloneDeep(collaborators);
        const protectedOptions = collaboratorsCopy.filter((opt) => opt.readOnly || opt.isFixed);

        const unprotectedOptions = selectedOptions.filter(
            (opt) => !protectedOptions.map((pOpt) => pOpt.guid).includes(opt.guid)
        );

        const combinedOptions = unprotectedOptions.concat(protectedOptions);

        setCollaborators(combinedOptions);
    };

    const handleReadersChange = (selectedOptions: IMultiSelectOption[]) => {
        const readersCopy = cloneDeep(readers);
        const protectedOptions = readersCopy.filter((opt) => opt.readOnly || opt.isFixed);

        const unprotectedOptions = selectedOptions.filter(
            (opt) => !protectedOptions.map((pOpt) => pOpt.guid).includes(opt.guid)
        );

        const combinedOptions = unprotectedOptions.concat(protectedOptions);

        setReaders(combinedOptions);
    };

    const handleReportNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (value?.length > 0) {
            setValidation((prev) => {
                let newValidation = {};
                if (FormFields.ReportName in prev) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { [FormFields.ReportName]: _, ...rest } = prev;
                    newValidation = rest;
                }
                return newValidation;
            });
        }

        setReportName(value);
    };

    const handleReportStatusChange = (option: any) => {
        if (option.id) {
            setValidation((prev) => {
                let newValidation = {};
                if (FormFields.ReportStatus in prev) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { [FormFields.ReportStatus]: _, ...rest } = prev;
                    newValidation = rest;
                }
                return newValidation;
            });
        }

        setReportStatusId(option.id);
    };

    const handleEditClick = () => {
        // obtain lease and then make form editable

        if (!props.data?.report?.guid) return;

        const durationInMs = props.data?.leaseDuration;

        obtainEntityLeaseMutation
            .mutateAsync({
                entityTypeId: EntityTypes.Report,
                entityGuid: props.data?.report?.guid,
                durationInMs,
            })
            .then((result) => {
                if (result?.success) {
                    setLocalReadonly(false);
                    props.data?.onLeaseObtained(result.currentLease);
                } else if (result) {
                    props.data?.onFailedToObtainLease(result.currentLease);
                }
            })
            .catch(() => {
                showErrorToast(`Failed to obtain lease for editing report - ${props.data?.report?.name}`);
            });
    };

    useEffect(() => {
        if (reportStatusTypes.isSuccess) {
            const reportStatusOptions = CheckmateSelectHelper.getLookupOptions(
                reportStatusTypes.data ?? [],
                false
            );
            setReportStatusOptions(reportStatusOptions);
        }
    }, [reportStatusTypes.isSuccess]);

    useEffect(() => {
        setValidation({});
    }, [props.open]);

    useEffect(() => {
        if (props.data?.readonly) {
            setCollaborators((prev) => {
                return prev.map((user) => ({ ...user, isFixed: true }));
            });
        }
    }, [props.data?.readonly]);

    const availableReaders = props.data?.availableUsers
        .filter((availUser) => availUser.guid !== auth.user.guid)
        .filter(
            (availUser) =>
                !collaborators.map((collabUser) => collabUser.guid).includes(availUser.guid)
        );

    const showEditButton = props.data?.showEditButton && localReadonly;

    collaborators.sort((a, b) => (a.label < b.label ? -1 : 1));
    availableReaders?.sort((a, b) => (a.label < b.label ? -1 : 1));

    return (
        <Modal centered show={props.open} backdrop>
            <Modal.Header>
                <Modal.Title>{props.data?.title ?? 'Save Report'}</Modal.Title>
                {showEditButton && (
                    <IconButton
                        buttonCssClass="mr-0 btn-no-bg"
                        icon="fal fa-pen"
                        onClick={handleEditClick}
                        text="Edit"
                    />
                )}
            </Modal.Header>
            <Modal.Body>
                <div className="row mb-2">
                    <div className="col-sm-3">
                        <label className="control-label">Report Name*</label>
                    </div>
                    <div className="col-sm-9">
                        <input
                            type="text"
                            className="form-control"
                            disabled={localReadonly}
                            value={reportName}
                            onChange={handleReportNameChange}
                        />
                        <div className="text-danger mb-1">{validation[FormFields.ReportName]}</div>
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-sm-3">
                        <label className="control-label">Report Status*</label>
                    </div>
                    <div className="col-sm-9">
                        <CheckmateSelect
                            isDisabled={
                                localReadonly ||
                                props.data?.report?.status?.id === ReportStatusTypesEnum.Published
                            }
                            options={reportStatusOptions}
                            value={reportStatusOptions.find((x) => x.id === reportStatusId)}
                            onChange={handleReportStatusChange}
                        />
                        <div className="text-danger mb-1">
                            {validation[FormFields.ReportStatus]}
                        </div>
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-sm-3">
                        <label className="control-label">Collaborator(s)</label>
                    </div>
                    <div className="col-sm-9">
                        <CheckmateSelect
                            isDisabled={localReadonly}
                            isMulti
                            notClearable
                            onChange={handleCollaboratorsChange}
                            options={props.data?.availableUsers ?? []}
                            value={collaborators}
                        />
                    </div>
                </div>
                {reportStatusId === ReportStatusTypesEnum.Published && (
                    <div className="row mb-2">
                        <div className="col-sm-3">
                            <label className="control-label">Reader(s)</label>
                        </div>
                        <div className="col-sm-9">
                            <CheckmateSelect
                                isDisabled={props.data?.disableReaders}
                                isMulti
                                notClearable
                                options={availableReaders ?? []}
                                value={readers}
                                onChange={handleReadersChange}
                            />
                        </div>
                    </div>
                )}
                {props.data?.report?.guid &&
                    props.data?.alertGuid &&
                    props.data?.reportCollaborators.length > 0 && (
                        <div className="mb-2">
                            <CollabActHistory
                                alertGuid={props.data?.alertGuid}
                                reportGuid={props.data?.report?.guid}
                                reportStatus={props.data?.report?.status?.id}
                                onCollabActClosed={props.data?.onCollabActClosed}
                                userStatuses={props.data?.userStatuses}
                            />
                        </div>
                    )}
            </Modal.Body>
            <Modal.Footer>
                {(!localReadonly ||
                    JSON.stringify(readers) !== JSON.stringify(originalReaders)) && (
                    <button className="btn btn-orange" onClick={handleSaveReport}>
                        Save
                    </button>
                )}
                <button className="btn btn-default" onClick={handleCloseClick}>
                    {props.data?.closeButtonText}
                </button>
            </Modal.Footer>
        </Modal>
    );
};
